import { motion } from "framer-motion";
import { Star } from "lucide-react";
import { useI18n } from "@/lib/i18n";

const ServicesSection = () => {
  const { lang, t } = useI18n();
  const items = t.services.items[lang];

  return (
    <section id="what" className="py-16 md:py-20 bg-brand-off">
      <div className="container mx-auto px-6 max-w-[960px]">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <div className="section-label mb-3">{t.services.label[lang]}</div>
          <h2 className="font-display text-3xl md:text-5xl text-foreground mb-5 leading-[1.1]">
            {t.services.h2[lang]}
          </h2>
          <p className="font-light text-[15px] leading-[1.7] text-brand-text max-w-[680px]">
            {t.services.sub[lang]}
          </p>

          <div className="grid sm:grid-cols-2 gap-5 mt-8">
            {items.map((item: any, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="bg-brand-black p-7 rounded-sm border-l-2 border-brand-blue/40"
              >
                <div className="flex items-baseline justify-between mb-4">
                  <div className="flex items-baseline gap-3">
                    <span className="font-display text-3xl text-brand-blue leading-none">
                      0{i + 1}
                    </span>
                    <span className="font-display text-xl text-white leading-none tracking-wide">
                      {item.name}
                    </span>
                  </div>
                  {item.step && (
                    <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-white/50">
                      {item.step}
                    </span>
                  )}
                </div>
                <p className="font-light text-[13.5px] leading-[1.6] text-white/80 mb-4">
                  {item.desc}
                </p>
                {item.output && (
                  <ul className="border-t border-white/10 pt-4 space-y-1.5">
                    {item.output.map((o: string) => (
                      <li
                        key={o}
                        className="flex items-start gap-2 text-[12.5px] text-white/70 leading-snug"
                      >
                        <span className="mt-1.5 w-1 h-1 rounded-full bg-brand-blue shrink-0" />
                        <span>{o}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="mt-5 bg-brand-black border border-brand-blue border-l-[3px] rounded-sm p-7 flex gap-7 items-start"
          >
            <Star className="text-brand-blue shrink-0" size={36} fill="currentColor" />
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.3em] text-white/40 mb-2">
                {t.services.event.title[lang]}
              </div>
              <p className="font-light text-[13.5px] leading-[1.65] text-white/80">
                {t.services.event.desc[lang]}
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.35 }}
            className="mt-5 bg-brand-off border border-brand-blue/30 rounded-sm p-7 flex gap-5 items-start"
          >
            
            <div>
              <div className="flex items-baseline gap-3 mb-2 flex-wrap">
                <span className="font-display text-xl text-foreground leading-none tracking-wide">
                  {t.services.addon.name[lang]}
                </span>
                <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-brand-blue">
                  {t.services.addon.tag[lang]}
                </span>
              </div>
              <p className="font-light text-[13.5px] leading-[1.65] text-brand-text">
                {t.services.addon.desc[lang]}
              </p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesSection;
